import { FC } from 'react';
import { Heading, RTEditor, ContentLoader, Text } from '@frontend/design-system';
import { useGetCompanyLogo, useGetLegalContent } from '../../../../hooks/api-hooks';
import { CompanyLogo } from '../../../kiosk/components/kiosk-layout/company-logo';
import { useLegalContext } from '../../provider';
import { legalCardStyles, legalContainerStyles, legalTextStyles, errorStyles } from './legal-page-content.styles';

// Helper function to check if content is HTML
const isHTML = (text: string): boolean => {
  const div = document.createElement('div');
  div.innerHTML = text;

  for (let c = div.childNodes, i = c.length; i--; ) {
    if (c[i].nodeType == 1) return true;
  }

  return false;
};

// Convert plain text to HTML
const getHTMLText = (text: string): string => {
  if (isHTML(text)) {
    return text;
  }

  const textarea = document.createElement('pre');
  textarea.innerHTML = text;
  return textarea.innerText.replace(/\n/g, '<br />');
};

export const LegalPageContent: FC = () => {
  const { documentType, locationId } = useLegalContext();
  const { legalContentInfo, isLoading, isError } = useGetLegalContent(locationId, documentType);
  const { logoUrl } = useGetCompanyLogo(locationId);

  if (isLoading) {
    return <ContentLoader show />;
  }

  if (isError) {
    return (
      <section css={legalContainerStyles}>
        <div css={legalCardStyles}>
          <Text css={errorStyles}>{legalContentInfo.error}</Text>
        </div>
      </section>
    );
  }

  return (
    <section css={legalContainerStyles}>
      <header>
        <CompanyLogo logoUrl={logoUrl} />
        <Heading level={2} textAlign='center'>
          {legalContentInfo.header}
        </Heading>
      </header>

      <div css={legalCardStyles}>
        <div css={legalTextStyles}>
          {legalContentInfo.content ? (
            <RTEditor
              initialHTML={getHTMLText(legalContentInfo.content)}
              readOnly
              uniqueId={`legal-${documentType}-${locationId}`}
            >
              <RTEditor.Editor />
            </RTEditor>
          ) : (
            <Text css={errorStyles}>{legalContentInfo.error}</Text>
          )}
        </div>
      </div>
    </section>
  );
};
